import { useEffect } from 'react';

import pick from 'lodash/fp/pick';

import { useTracking } from '@change/core/react/tracking';
import { getWindowSafe } from '@change/core/window';

import { useQueryString } from 'src/app/shared/hooks/location';

import { useCaptchaEnabled } from '../captcha';
import { usePetitionDetails } from '../petitionDetails';
import { useUserInfo } from '../userInfo';

import type { TrackingPetitionStatus } from './shared/petitionStatus';
import { useTrackingPetitionStatus } from './shared/petitionStatus';

type ViewEventQueryParams =
	| 'algorithm'
	| 'fb_action_types'
	| 'fb_ref'
	| 'grid_position'
	| 'original_footer_petition_id'
	| 'recruit_context'
	| 'recruit_variation'
	| 'source_location';
type ViewEventQueryParamsProp = Readonly<Partial<Record<ViewEventQueryParams, string>>>;
type ViewEventProps = Readonly<{
	captcha: boolean;
	category_tags: string[];
	geo_reach?: string;
	movement_id: string;
	own_petition: boolean;
	petition_id: string;
	petition_status: TrackingPetitionStatus;
	petition_strength_score: number;
	promotable: boolean;
	recent_signers_count: number;
	relevant_location_localized_name?: string;
	show_sign: boolean;
	signature_count: number;
	signed: boolean;
	video: boolean;
	viewport_height?: number;
	viewport_width?: number;
}> &
	ViewEventQueryParamsProp;

function queryParams(query: ViewEventQueryParamsProp) {
	const gridPosition = !!query.grid_position && query.grid_position.match(/^\d+$/) ? query.grid_position : undefined;

	return {
		grid_position: gridPosition,
		...pick([
			'source_location',
			'algorithm',
			'original_footer_petition_id',
			'fb_ref',
			'fb_action_types',
			'recruit_variation',
			'recruit_context',
		])(query),
	};
}

function viewportTrackingParams() {
	const viewport = getWindowSafe()?.visualViewport;
	if (!viewport) {
		return undefined;
	}
	return { viewport_width: viewport.width, viewport_height: viewport.height };
}

export function useTrackPageView(): void {
	const {
		id,
		promotable,
		movements,
		score,
		signatureState: { signatureCount },
		tagsConnection,
		geoReach,
		relevantLocationLocalizedName,
		recentSignersConnection,
	} = usePetitionDetails();
	const movementId = movements.nodes[0]?.id;
	const { signed, ownPetition } = useUserInfo();
	const track = useTracking();

	const captcha = useCaptchaEnabled();
	const query = useQueryString();
	const status = useTrackingPetitionStatus();

	useEffect(() => {
		void track<ViewEventProps>('petition_view', {
			petition_id: `petition_${id}`,
			signed,
			movement_id: movementId,
			promotable,
			petition_status: status,
			captcha,
			video: false,
			signature_count: signatureCount.displayed,
			own_petition: ownPetition,
			petition_strength_score: score,
			show_sign: query.show_sign === 'true',
			category_tags: tagsConnection?.nodes?.map((tag) => tag.name) ?? [],
			geo_reach: geoReach ?? undefined,
			relevant_location_localized_name: relevantLocationLocalizedName ?? undefined,
			recent_signers_count: recentSignersConnection.edges.length,
			...viewportTrackingParams(),
			...queryParams(query),
		});
		// only send the view event once per id
		// for instance, we don't want a change in the query params to trigger a new event
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);
}
